<template>
    <div>
        <h2 class="u-margin-top-30 u-margin-bottom-30">
            Purchase - {{ purchase.description }}
        </h2>

        <div class="flex-grid flex-grid--reduced-bottom-margin a-form-inputs">
            <div class="flex-grid__item">
                <control for="purchase_description" label="Purchase Description">
                        <input autocomplete="off" v-model="purchase.description"
                            required
                            id="purchase_description"
                            name="purchase_description"
                            v-field-control:purchase_description.required="{type:'text'}"
                            type="text">
                    <control-error :show="$error('purchase_description')">
                        The purchase description is required
                    </control-error>
                </control>
                <control for="purchase_date" label="Initial Purchase Date">
                    <date-input v-model="purchase.purchaseDate"
                                v-field-control:purchase_date.required="{type:'date'}"
                                @input="updateAgeAtPurchase"
                    ></date-input>
                    <control-error :show="$error('purchase_date')">
                        The purchase date is required and must be in the format DD/MM/YYYY
                    </control-error>
                    <control-error :show="!purchaseDateValidation">
                        The purchase date must be after the date of assumed settlement {{ getClaimInfo('dateOfAssumedSettlement') }}
                    </control-error>
                </control>
                <control for="purchase_until" label="Purchase Until" v-if="purchase.type !== 'one'">
                    <select id="purchase_until"
                            v-model="purchase.purchaseUntil"
                            @change="purchaseUntilType"
                            v-field-control
                            required>
                        <option :value="null" disabled hidden>Please select...</option>
                        <option :value="'retirement'">Retirement</option>
                        <option :value="'life'">'But For' Life Expectancy</option>
                        <option :value="'own'">Enter Own Date</option>
                    </select>
                </control>
                <life-expectancy
                        v-if="purchase.purchaseUntil === 'life' && purchase.type !== 'one'"
                        label="Claimant's 'But For' Life Expectancy"
                        :person="thePerson"
                        :index="index"
                        :date="theDate"
                        :assumedSettlement="getClaimInfo('dateOfAssumedSettlement')"
                        type="claimant"
                        @update="updateLife"
                ></life-expectancy>
                <control for="purchase_end" label="Purchase End Date" v-if="purchase.type !== 'one'">
                    <date-input v-model="purchase.purchaseEnd"
                                v-field-control:purchase_end.required="{type:'date'}"
                                @input="setPurchase()"
                                :disabled="purchase.purchaseUntil === 'life'"
                    ></date-input>
                    <control-error :show="$error('purchase_date')">
                        The purchase end date is required and must be in the format DD/MM/YYYY
                    </control-error>
                    <control-error :show="!purchaseEndDateValidation">
                        The purchase end date must be after the purchase date {{ purchase.purchaseDate }}
                    </control-error>
                </control>
                <control for="purchase_multiplicand" label="Purchase Multiplicand" before-after="pound">
                    <input autocomplete="off" v-model.number="purchase.multiplicand"
                            required
                            v-two-dp
                            min="1"
                            id="purchase_multiplicand"
                            v-field-control:purchase_multiplicand.required="{type:'number', rules:'min:0.01'}"
                            v-on:focus="purchase.multiplicand = purchase.multiplicand || ''"
                            v-on:blur="purchase.multiplicand = purchase.multiplicand || 0"
                            @input="setPurchase"
                            type="text">
                    <control-error :show="$error('purchase_multiplicand')">
                        The purchase multiplicand is required and has to be greater than 0
                    </control-error>
                </control>
                <control for="mortality_multiplier" label="Adjust Multiplier for Mortality?">
                    <select id="mortality_multiplier"
                            v-model="purchase.mortalityMultiplier"
                            @change="setPurchase"
                            v-field-control
                            required>
                        <option :value="null" disabled hidden>Please select...</option>
                        <option :value="true">Yes</option>
                        <option :value="false">No</option>
                    </select>
                </control>
            </div>
            <div class="flex-grid__item flex-grid--space-left">
                <control for="purchase_type" label="Purchase Type">
                    <select id="purchase_type"
                            v-model="purchase.type"
                            v-field-control
                            @change="setPurchaseType"
                            required>
                        <option :value="null" disabled hidden>Please select...</option>
                        <option :value="'one'">One Off</option>
                        <option :value="'recurring'">Recurring</option>
                        <option :value="'continuous'">Continuous</option>
                    </select>
                </control>
                <control for="age_at_purchase" label="Age at Initial Purchase" before-after="years">
                    <input autocomplete="off" v-model.number="purchase.ageAtPurchase"
                            required
                            min="1"
                            id="age_at_purchase"
                            v-field-control:age_at_purchase.required="{type:'number', rules:'min:0.01'}"
                            @input="updateDateOfPurchase"
                            type="text">
                    <control-error :show="$error('age_at_purchase')">
                        Age at purchase is required and has to be greater than 0
                    </control-error>
                </control>
                <control label="Purchase Frequency" v-if="purchase.type === 'recurring'" before-after="years">
                        <input autocomplete="off" v-model.number="purchase.frequency"
                                required
                                min="1"
                                id="purchase_frequency"
                                v-field-control:purchase_frequency.required="{type:'number', rules:'min:0.01'}"
                                @input="setPurchase"
                                type="text">
                </control>
                <control for="resale_option" label="Is There a Resale Value?" v-if="purchase.type !== 'continuous'">
                    <select id="resale_option"
                            v-model="purchase.resaleOption"
                            v-field-control
                            @change="updateResaleOption"
                            required>
                        <option :value="null" disabled hidden>Please select...</option>
                        <option :value="true">Yes</option>
                        <option :value="false">No</option>
                    </select>
                </control>
                <control for="resale_value" label="Resale Value" v-if="purchase.resaleOption" before-after="pound" >
                    <input autocomplete="off" v-model.number="purchase.resaleValue"
                            required
                            v-two-dp
                            min="1"
                            id="resale_value"
                            v-field-control:resale_value.required="{type:'number', rules:'min:0.01'}"
                            @input="setPurchase"
                            type="text">
                    <control-error :show="$error('age_at_purchase')">
                        Resale value is required and has to be greater than 0
                    </control-error>
                    <control-error :show="!resaleValueValidation">
                        The resale value must be less that the initial purchase multiplicand
                    </control-error>
                </control>
                <control for="purchase_total"
                            label="Total"
                            before-after="pound"
                            :tip-show="purchase.total > 0"
                            :tip-icon="'formula'"
                            tip-text="Click to view calculation"
                            tip-side="left"
                            tip-width="140"
                            tip-name="purchase_multiplier"
                            :modal-name="'multipliers_' + uniqueId"
                            :endpoint="'purchase'"
                            :payload="purchasePayload">
                    <input autocomplete="off" v-model.number="purchase.total"
                            required
                            disabled
                            v-two-dp
                            min="1"
                            id="purchase_total"
                            v-field-control:purchase_total.required="{type:'number', rules:'min:0.01'}"
                            type="text">
                </control>
            </div>
        </div>
        
        <button type="button" class="a-button a-button--no-margin" @click="deletePurchase">Remove Purchase</button>

        <modal :name="'multipliers_' + uniqueId" no-default-button="true">
            <modal-content></modal-content>
        </modal>
    </div>
</template>
<script>
    import Control from 'components/form/Control.vue';
    import ControlError from 'components/form/control/Error.vue';
    import currencyFormat from 'mixins/currencyFormat';
    import DateInput from 'components/form/control/DateInput.vue';
    import FormValidator from 'components/form/validator/mixin';
    import roundValue from 'mixins/roundValue';
    import TwoDecimalPlaces from 'directives/TwoDecimalPlaces';
    import {mapActions, mapGetters} from 'vuex';
    import LifeExpectancy from 'components/LifeExpectancy.vue';
    import Modal from 'components/modal/Modal.vue';
    import ModalContent from 'components/modal/ModalContent.vue';

    export default {
        name: 'future-purchase',
        props: {
            index: {
                type: Number,
                default: 0
            },
            purchase: {
                type: Object,
                required: true
            },
        },
        mixins: [
            FormValidator,
            currencyFormat,
            roundValue,
        ],
        components: {
            Control,
            ControlError,
            DateInput,
            LifeExpectancy,
            Modal,
            ModalContent
        },
        directives: {
            'two-dp': TwoDecimalPlaces
        },
        data() {
            return {
                theDate: moment().format('DD/MM/YYYY'),
            }
        },
        computed: {
            ...mapGetters('personal/otherFutureLosses', [
                'value',
                'purchaseValue'
            ]),
            ...mapGetters('personal/claimant', [
                'claimant',
                'getClaimInfo',
                'getClaimantValue',
                'getIncomeValue'
            ]),
            purchaseDateValidation: {
                get() {
                    let mPurchaseDate = moment(this.purchase.purchaseDate, 'DD/MM/YYYY');
                    let mDateOfAssessment = moment(this.getClaimInfo('dateOfAssumedSettlement'), 'DD/MM/YYYY');

                    if (!mPurchaseDate.isValid() || !mDateOfAssessment.isValid()) {
                        return false;
                    }

                    return mPurchaseDate.isSameOrAfter(mDateOfAssessment);
                }
            },
            purchaseEndDateValidation: {
                get() {
                    let mPurchaseDate = moment(this.purchase.purchaseDate, 'DD/MM/YYYY');
                    let mPurchaseEndDate = moment(this.purchase.purchaseEnd, 'DD/MM/YYYY');

                    if (!mPurchaseDate.isValid() || !mPurchaseEndDate.isValid()) {
                        return false;
                    }

                    return mPurchaseDate.isBefore(mPurchaseEndDate);
                }
            },
            resaleValueValidation: {
                get() {
                    return (this.purchase.resaleValue < this.purchase.multiplicand);
                }
            },
            updateAgeAtPurchase() {
                if (this.purchase.purchaseDate !== null && this.purchase.purchaseDate !== '') {
                    let mPurchaseDate = moment(this.purchase.purchaseDate, 'DD/MM/YYYY');
                    let mDateOfBirth = moment(this.getClaimantValue('dateOfBirth'), 'DD/MM/YYYY');
                    this.purchase.ageAtPurchase = mPurchaseDate.diff(mDateOfBirth, 'years', true).toFixed(2);
                    this.setPurchase();
                }
            },
            updateResaleOption() {
                if (!this.purchase.resaleOption){
                    this.purchase.resaleValue = 0
                }
                this.setPurchase()
            },
            thePerson() {
                return {
                    lifeExpectancy: this.value('purchasesLifeExpectancy'),
                    gender: this.claimant.gender,
                    dateOfBirth: this.claimant.dateOfBirth
                };
            },
            uniqueId() {
                return this._uid;
            },
            purchasePayload() {
                return {
                    claimInfo: {
                        date_of_assumed_settlement: this.getClaimInfo('dateOfAssumedSettlement'),
                        discount_rate: this.getClaimInfo('discountRate'),
                        date_of_birth: this.claimant.dateOfBirth,
                        gender: this.claimant.gender,
                    },
                    purchase: this.purchase,
                }
            },
        },
        methods: {
            ...mapActions('personal/otherFutureLosses', [
                'set',
                'updatePurchase',
                'removePurchase',
                'updateLifeForPurchases'
            ]),
            purchaseUntilType() {
                if (this.purchase.purchaseUntil === 'retirement') {
                    this.purchase.purchaseEnd = this.getIncomeValue('retirementDate')
                }
                if (this.purchase.purchaseUntil === 'life') {
                    let lifeExpectancy = this.value('purchasesLifeExpectancy');
                    this.updateLifeForPurchases({lifeExpectancy, index: this.index})
                }
                this.setPurchase();
            },
            updateDateOfPurchase() {
                if (!this.purchase.ageAtPurchase) {
                    return;
                }

                // cap age between 0 and 140.
                if (this.purchase.ageAtPurchase < 0) {
                    this.purchase.ageAtPurchase = 0;
                    return;
                } else  if (this.purchase.ageAtPurchase > 140) {
                    this.purchase.ageAtPurchase = 140;
                }

                let mDateOfBirth = moment(this.getClaimantValue('dateOfBirth'), 'DD/MM/YYYY');
                this.purchase.purchaseDate = mDateOfBirth.add(this.purchase.ageAtPurchase, 'years').format('DD/MM/YYYY');
                this.setPurchase();

                // make sure age is set to 2dp when returned back to the form
                this.purchase.ageAtPurchase = parseFloat(this.purchase.ageAtPurchase).toFixed(2);
            },
            updateLife(lifeExpectancy) {
                this.updateLifeForPurchases({lifeExpectancy, index: this.index})
            },
            deletePurchase() {
                this.removePurchase({index: this.index})
            },
            setPurchase() {
                this.updatePurchase({index: this.index})
            },
            setPurchaseType() {
                this.purchase.total = 0;
                this.purchase.discount = 0;
                this.updatePurchase({index: this.index})
            }
        },
    }
</script>


// WEBPACK FOOTER //
// FuturePurchase.vue?29a93862